import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import GenericCard from '../../../../../shared/components/v3/Cards/GenericCard/GenericCard';
import GenericCarousel from '../../../../../shared/components/v3/GenericCarousel/GenericCarousel';
import { BENEFIT_DETAIL_ROUTE, BENEFITS_TO_CHOOSE_ROUTE } from '../../../../../shared/constants/auth';
import { TRANSLATIONS_BENEFIT_CATEGORIES } from '../../../../../shared/constants/benefits';
import { NORTH_EAST_ARROW } from '../../../../../shared/constants/images';
import { UPGRADE_BENEFITS } from '../../../../../shared/constants/plan';
import {
  CLICK_ACTION,
  SCREEN_MY_PLANS,
  SELECTED_BENEFITS_DETAIL,
  WELLBEING_BENEFIT_DETAIL,
  WELLBEING_BENEFIT_FILTER
} from '../../../../../shared/constants/tagManager';
import { PlansContext } from '../../../../../shared/context/plans/plansContext';
import { ICompanyBenefit, MyPlanSectionProps } from '../../../../../shared/interfaces/plans';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATION_MY_PLAN } from '../../../../../shared/translations/plan';
import { categoryKeys } from '../../../../../shared/types/benefitTypes';

const BenefitsCard = ({ benefitsState, modalHandler, refetch }: MyPlanSectionProps) => {
  const [elements, setElements] = useState<JSX.Element[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { assingBfuChoices, assingSelectedBenefit } = useContext(PlansContext);

  const { categories, tabs } = useMemo(() => {
    const categories = Object.keys(benefitsState.data.benefits)
      .filter((category) => category !== 'health')
      .sort((a, b) => a.localeCompare(b));
    const tabs = categories.map((category) => ({
      value:
        category === 'all'
          ? 'global.label.all'
          : (TRANSLATIONS_BENEFIT_CATEGORIES[category as categoryKeys] ?? category)
    }));
    return { categories, tabs };
  }, [benefitsState]);

  const handleClickBfu = (bfuChoices: number) => {
    assingBfuChoices(bfuChoices);
    TagManagerService.pushEvent(CLICK_ACTION, SELECTED_BENEFITS_DETAIL, SCREEN_MY_PLANS);
    navigate(BENEFITS_TO_CHOOSE_ROUTE);
  };

  const handleClickBenefit = (benefit: ICompanyBenefit) => {
    assingSelectedBenefit(benefit);
    const gtmParams = {
      ...SCREEN_MY_PLANS,
      benefit_sku: benefit.sku,
      benefit_id: benefit.benefitId,
      provider: benefit.providerName
    };
    TagManagerService.pushEvent(CLICK_ACTION, WELLBEING_BENEFIT_DETAIL, gtmParams);
    navigate(BENEFIT_DETAIL_ROUTE);
  };

  const setBenefits = useCallback(
    (index: number) => {
      const selectedCategory = categories[index];
      const benefits: JSX.Element[] = [];
      if (benefitsState.data.bfuChoices) {
        benefits.push(
          <GenericCard
            key={uuid()}
            title={t(TRANSLATION_MY_PLAN.cards.benefit.bfuTitle, {
              number: benefitsState.data.bfuChoices,
              count: benefitsState.data.bfuChoices
            })}
            size="m"
            iconButton={NORTH_EAST_ARROW}
            className="bfu-card"
            action={() => handleClickBfu(benefitsState.data.bfuChoices)}
          />
        );
      }
      const selectedBenefits = benefitsState.data.benefits[selectedCategory];

      if (selectedBenefits?.length) {
        benefits.push(
          ...selectedBenefits.map((benefit) => (
            <GenericCard
              key={uuid()}
              title={benefit.title}
              description={benefit.providerName}
              size="m"
              icon={benefit.icon}
              iconButton={NORTH_EAST_ARROW}
              action={() => handleClickBenefit(benefit)}
            />
          ))
        );
      }
      setElements(benefits);
    },
    [categories, benefitsState, t]
  );

  const onTabChange = (index: number) => {
    setBenefits(index);
    const gtmParams = {
      ...SCREEN_MY_PLANS,
      tab: tabs[index]
    };
    TagManagerService.pushEvent(CLICK_ACTION, WELLBEING_BENEFIT_FILTER, gtmParams);
  };

  useEffect(() => {
    setBenefits(0);
  }, [setBenefits]);

  if (benefitsState.error) {
    return (
      <div className="section-error">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={refetch}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          className="flex  justify-center"
        />
      </div>
    );
  }

  if (benefitsState.isLoading) {
    return <Skeleton variant="rectangular" className="section-skeleton" />;
  }

  if (!elements.length) {
    return null;
  }

  return (
    <div className="carousel-container flex flex-col justify-center items-center">
      <GenericCarousel
        sliders={elements}
        topData={{
          title: t(TRANSLATION_MY_PLAN.cards.benefit.title),
          description: t(TRANSLATION_MY_PLAN.cards.benefit.description)
        }}
        filters={{
          showFilter: true,
          items: tabs,
          onChange: onTabChange
        }}
        buttonData={{
          text: t(TRANSLATION_MY_PLAN.cards.benefit.button),
          action: () => modalHandler(UPGRADE_BENEFITS)
        }}
      />
    </div>
  );
};

export default BenefitsCard;
